<template>
  <section
    v-if="items.data.count !== 0 && enoughItems"
    class="rc-carousel-hooper"
    :class="bgGradient ? 'my-0 py-0' : ''"
  >
    <div
      :class="
        bgGradient && items.data.results.length ? 'rc-container-gradient' : ''
      "
    >
      <transition :name="fadeTransition">
        <div
          v-if="items.data.status == 'success'"
          :class="fluid ? 'container-fluid' : 'container'"
        >
          <div class="rc-carousel-hooper--header">
            <template v-if="title">
              <h2 class="h3">
                {{ title }}
              </h2>
            </template>
            <template v-if="to || items.data.to">
              <router-link
                class="rc-link-secondary"
                :to="to || items.data.to"
                :title="title + ' (' + $t(`common.view_all`) + ')'"
                >{{ $t("common.view_all") }}</router-link
              >
            </template>
          </div>
        </div>
      </transition>
      <transition :name="fadeTransition">
        <template v-if="items && items.data.results.length">
          <RcCarousel
            :items="items"
            :settings="settings"
            :unitType="items.unitType"
            :variant="items.variant"
            :isStacked="items.isStacked"
          />
        </template>
      </transition>
      <template
        v-if="
          skeleton &&
          (!items.data.results.length || items.data.status == 'fetching')
        "
      >
        <RcCarouselSkeleton
          :count="6"
          :fluid="fluid"
          :settings="settings"
          @visible="onVisible"
        ></RcCarouselSkeleton>
      </template>
    </div>
  </section>
</template>

<script>
import { ITEM_GET_LIST_BY_FACET } from "@/libvue/apps/catalog/items/actions";
import RcCarouselSkeleton from "@/libvue/apps/catalog/items/CarouselSkeleton.vue";
import RcCarousel from "@/libvue/apps/catalog/items/Carousel.vue";
import { mapState } from "vuex";

export default {
  name: "RcCarouselCompound",

  components: {
    RcCarousel,
    RcCarouselSkeleton
  },

  props: {
    title: {
      type: String,
      default: ""
    },
    to: {
      type: Object,
      default: () => {}
    },
    promise: {
      type: String,
      default: ITEM_GET_LIST_BY_FACET
    },
    payload: {
      type: [Object, String],
      required: true
    },
    settings: {
      type: Object,
      default: () => ({})
    },
    objectType: {
      type: String,
      default: "item"
    },
    unitType: {
      type: String,
      default: "RcGridUnit"
    },

    omitUID: {
      type: String,
      default: ""
    },
    lazy: {
      type: Boolean,
      default: true
    },
    min: {
      type: Number,
      default: 3
    },
    skeleton: {
      type: Boolean,
      default: true
    },
    bgGradient: {
      type: Boolean,
      default: false
    },
    fluid: {
      type: Boolean,
      default: false
    }
  },
  emits: ["loaded"],
  data() {
    return {
      items: { data: { results: [] }, status: "", unitType: "RcGridUnit" },
      enoughItems: true
    };
  },
  computed: {
    ...mapState({ profile: (state) => state.auth.user }),
    fadeTransition() {
      //Transition only when no skeleton
      return !this.skeleton ? "fade-carousel" : "";
    }
  },
  mounted() {
    if (!this.lazy) {
      this.onVisible(true);
    }
  },
  methods: {
    makeTwoDimensional(array) {
      return array.reduce((grouped, genre, i) => {
        if (i % 2 === 0) {
          grouped.push([genre, array[i + 1] ? array[i + 1] : null]);
        }
        return grouped;
      }, []);
    },
    onVisible(isVisible) {
      if (isVisible) {
        this.items = { data: { results: [] }, status: "fetching" };
        this.$store
          .dispatch(this.promise, this.payload)
          .then(({ data }) => {
            this.$emit("loaded", data);
            if (this.objectType == "item") {
              this.items.data = data;
              this.items.data.status = "success";

              this.items.data.results = this.omitUID
                ? data.results.filter((i) => i.uid !== this.omitUID)
                : data.results;
              this.items.data.count = this.omitUID
                ? data.results.filter((i) => i.uid !== this.omitUID).length
                : data.count;

              this.items.unitType = this.unitType;

              if (this.items.data.count < this.min) {
                this.enoughItems = false;
              }
            } else if (this.objectType == "mGet") {
              this.items.data.status = "success";

              this.items.data.results = this.omitUID
                ? data.filter((i) => i.uid !== this.omitUID)
                : data;
              this.items.data.count = this.omitUID
                ? data.filter((i) => i.uid !== this.omitUID).length
                : data.count;

              this.items.unitType = this.unitType;

              if (this.items.data.count < this.min) {
                this.enoughItems = false;
              }
            } else if (this.objectType == "lineup") {
              this.items.data.status = "success";
              if (data.lineup_type == "items") {
                this.items.data.results = data.lineup_items;

                this.items.data.count = this.items.data.results.length;
                if (this.items.data.count < this.min) {
                  this.enoughItems = false;
                }
                this.items.data.to = {
                  name: "catalog.lineup.detail",
                  params: { slug: data.slug, locale: this.$i18n.locale }
                };
              } else if (data.lineup_type == "themes") {
                this.items.data.results = data.lineup_themes;
                this.items.data.count = data.lineup_themes.length;
                this.items.unitType = "RcCardUnit";
                this.items.variant = "theme";
                this.items.isStacked = true;
                this.items.data.to = {
                  name: "catalog.themes.list",
                  params: { locale: this.$i18n.locale }
                };
              } else if (data.lineup_type == "categories") {
                this.items.data.results = this.makeTwoDimensional(
                  data.lineup_categories
                );
                this.items.data.count = data.lineup_categories.length;

                this.items.unitType = "RcGridCategoryUnit";
              }
            }
          })
          .catch(() => {
            this.items.data = { results: [], count: 0, status: "error" };
          });
      }
    }
  }
};
</script>

<style lang="scss">
.rc-carousel-hooper .rc-ribbon {
  display: none;
}
.fade-carousel-enter-active,
.fade-carousel-leave-active {
  transition: opacity 1.5s;
}
.fade-carousel-enter,
.fade-carousel-leave-to {
  opacity: 0;
}
</style>
